import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Puzzle,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Globe,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth-context';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: Building2, label: 'Clientes', href: '/clients' },
  { icon: CreditCard, label: 'Planos', href: '/plans' },
  { icon: Puzzle, label: 'Recursos', href: '/features' },
  { icon: Globe, label: 'Idiomas', href: '/languages' },
  { icon: Users, label: 'Administradores', href: '/admins' },
  { icon: Settings, label: 'Configuracoes', href: '/settings' },
];

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const { logout, user } = useAuth();

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-gradient-to-b from-[#0D1E5E] to-[#0a1545] transition-all duration-300 ease-in-out',
        collapsed ? 'w-20' : 'w-72'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className={cn(
          'flex h-20 items-center border-b border-white/10',
          collapsed ? 'justify-center px-2' : 'px-6'
        )}>
          {collapsed ? (
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-[#0057FF]/30">
              <span className="text-white font-black text-lg">N</span>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-[#0057FF]/30">
                <span className="text-white font-black text-lg">N</span>
              </div>
              <div>
                <span className="text-2xl font-black text-white tracking-tight">NOZ</span>
                <span className="ml-2 px-2 py-0.5 rounded-md bg-white/10 text-[10px] font-medium text-white/70 uppercase tracking-wider">Admin</span>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className={cn('flex-1 py-6', collapsed ? 'px-3' : 'px-4')}>
          <div className="space-y-1.5">
            {menuItems.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-gradient-to-r from-[#0057FF] to-[#4F46E5] text-white shadow-lg shadow-[#0057FF]/30'
                      : 'text-white/70 hover:bg-white/10 hover:text-white',
                    collapsed && 'justify-center px-3'
                  )}
                >
                  <item.icon className={cn('h-5 w-5 shrink-0', isActive && 'drop-shadow-sm')} />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User Info */}
        {user && !collapsed && (
          <div className="mx-4 mb-4 p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center text-white font-semibold shadow-md">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                <p className="text-xs text-white/50 truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Logout Button */}
        <div className={cn('pb-6', collapsed ? 'px-3' : 'px-4')}>
          <button
            onClick={logout}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-white/70 hover:bg-red-500/20 hover:text-red-400 transition-all duration-200',
              collapsed && 'justify-center px-3'
            )}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>

        {/* Toggle Button */}
        <button
          onClick={onToggle}
          className="absolute -right-3 top-24 h-6 w-6 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </aside>
  );
}
